import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import logger from '../../logger'
import { useApi } from '../../composables/api'
import {
  base64UrlToUint8Array,
  bufferToBase64Url,
  pushSupportState,
  type PushSupportState
} from '../../utils/push-support'

export type PushActionResult = { ok: true } | { ok: false; message: string }

const REGISTRATION_WAIT_MS = 5000
const FORGET_DEVICE_CAP_MS = 2000

const ENABLE_ERROR = 'Could not turn on push notifications. Please try again.'
const DISABLE_ERROR = 'Could not turn off push notifications. Please try again.'
const BLOCKED_MESSAGE =
  'Notifications are blocked for this site. Allow them in your browser settings.'
const NO_WORKER_MESSAGE = 'Push notifications are not ready yet. Reload the app and try again.'

function readSupport(): PushSupportState {
  return pushSupportState(navigator, window)
}

function sameKey(existing: ArrayBuffer | null | undefined, wanted: Uint8Array): boolean {
  if (!existing) return false
  const bytes = new Uint8Array(existing)
  return bytes.length === wanted.length && bytes.every((b, i) => b === wanted[i])
}

/** The service worker registration, or null when none becomes ready (dev server, worker blocked). */
async function registrationOrNull(
  waitMs = REGISTRATION_WAIT_MS
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), waitMs))
  return Promise.race([navigator.serviceWorker.ready, timeout])
}

/**
 * Web Push for this device. `enable()` is the only place that asks the browser for notification permission, so it only ever
 * happens after the user presses Turn on. `resync()` keeps an existing subscription tied to whoever is signed in and never asks.
 */
export const usePushStore = defineStore('push', () => {
  const { meApi } = useApi()
  const { meApi: quietMeApi } = useApi({ notifyOnError: false })

  /** The server has VAPID keys. False hides the card. */
  const enabled = ref(false)
  const publicKey = ref('')
  const configLoaded = ref(false)
  /** This browser has a push subscription registered with the server. */
  const subscribed = ref(false)
  const support = ref<PushSupportState>('unsupported')
  const busy = ref(false)

  const canTurnOn = computed(
    () =>
      enabled.value &&
      !subscribed.value &&
      (support.value === 'default' || support.value === 'granted')
  )

  async function loadConfig() {
    try {
      const config = await quietMeApi.meGetPushConfig()
      enabled.value = config.enabled === true && !!config.publicKey
      publicKey.value = config.publicKey ?? ''
    } catch (error) {
      logger.error('Failed to load push configuration', error)
      enabled.value = false
    } finally {
      configLoaded.value = true
    }
  }

  /** Reads the browser's permission and whether this device already holds a subscription. Never prompts. */
  async function refreshDevice() {
    support.value = readSupport()
    if (support.value !== 'granted') {
      subscribed.value = false
      return
    }

    const registration = await registrationOrNull()
    subscribed.value = (await registration?.pushManager.getSubscription()) != null
  }

  async function postSubscription(subscription: PushSubscription, api: typeof meApi) {
    const json = subscription.toJSON()
    await api.meSavePushSubscription({
      savePushSubscriptionRequest: {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: json.keys?.p256dh ?? bufferToBase64Url(subscription.getKey('p256dh')),
          auth: json.keys?.auth ?? bufferToBase64Url(subscription.getKey('auth'))
        },
        expirationTime: subscription.expirationTime ? new Date(subscription.expirationTime) : null
      }
    })
  }

  async function subscribeAndSave(registration: ServiceWorkerRegistration, api: typeof meApi) {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(publicKey.value)
    })
    try {
      await postSubscription(subscription, api)
    } catch (error) {
      // Do not keep a browser subscription the server never heard about.
      await subscription.unsubscribe().catch(() => false)
      throw error
    }
  }

  /** The Turn on button. The only caller of `Notification.requestPermission()`. */
  async function enable(): Promise<PushActionResult> {
    if (!canTurnOn.value) return { ok: false, message: ENABLE_ERROR }

    busy.value = true
    try {
      if (support.value === 'default') {
        const permission = await Notification.requestPermission()
        support.value = readSupport()
        if (permission !== 'granted') return { ok: false, message: BLOCKED_MESSAGE }
      }

      const registration = await registrationOrNull()
      if (!registration) return { ok: false, message: NO_WORKER_MESSAGE }

      await subscribeAndSave(registration, meApi)
      subscribed.value = true
      return { ok: true }
    } catch (error) {
      logger.error('Failed to turn on push notifications', error)
      return { ok: false, message: ENABLE_ERROR }
    } finally {
      busy.value = false
    }
  }

  /** The Turn off button. Tells the server first, so a failure leaves the device subscribed and the button still usable. */
  async function disable(): Promise<PushActionResult> {
    busy.value = true
    try {
      const registration = await registrationOrNull()
      const subscription = await registration?.pushManager.getSubscription()
      if (subscription) {
        await meApi.meRemovePushSubscription({
          removePushSubscriptionRequest: { endpoint: subscription.endpoint }
        })
        await subscription.unsubscribe()
      }
      subscribed.value = false
      return { ok: true }
    } catch (error) {
      logger.error('Failed to turn off push notifications', error)
      return { ok: false, message: DISABLE_ERROR }
    } finally {
      busy.value = false
    }
  }

  /**
   * After sign-in: if this device already has a subscription and permission, make the server's copy match and belong to the
   * signed-in user (re-sending the same endpoint moves it). Resubscribes when the server's VAPID key changed. Never prompts.
   */
  async function resync() {
    try {
      await loadConfig()
      await refreshDevice()
      if (!enabled.value || support.value !== 'granted') return

      const registration = await registrationOrNull()
      const subscription = await registration?.pushManager.getSubscription()
      if (!registration || !subscription) {
        subscribed.value = false
        return
      }

      const wanted = base64UrlToUint8Array(publicKey.value)
      if (sameKey(subscription.options?.applicationServerKey, wanted)) {
        await postSubscription(subscription, quietMeApi)
      } else {
        // The server rotated its VAPID keys: the old subscription can never receive from the new key.
        await subscription.unsubscribe()
        await subscribeAndSave(registration, quietMeApi)
      }
      subscribed.value = true
    } catch (error) {
      logger.error('Failed to sync the push subscription', error)
    }
  }

  /**
   * On sign-out, so the next person on a shared device does not receive this user's notifications. Best effort and capped at
   * two seconds: logging out must never wait on the push service or the network.
   */
  async function forgetDevice() {
    // Without permission there can be no subscription, so skip waiting on a service worker that may not exist.
    if (readSupport() !== 'granted') {
      subscribed.value = false
      return
    }

    const work = (async () => {
      const registration = await registrationOrNull(FORGET_DEVICE_CAP_MS)
      const subscription = await registration?.pushManager.getSubscription()
      if (!subscription) return
      await quietMeApi.meRemovePushSubscription({
        removePushSubscriptionRequest: { endpoint: subscription.endpoint }
      })
      await subscription.unsubscribe()
    })().catch((error) => logger.error('Failed to forget this device for push', error))

    const cap = new Promise<void>((resolve) => setTimeout(resolve, FORGET_DEVICE_CAP_MS))
    await Promise.race([work, cap])
    subscribed.value = false
  }

  return {
    enabled,
    publicKey,
    configLoaded,
    subscribed,
    support,
    busy,
    canTurnOn,
    loadConfig,
    refreshDevice,
    enable,
    disable,
    resync,
    forgetDevice
  }
})
